import { PortfolioDto } from "../../../features/portfolio-info/types/PortfolioDto";
import { PortfolioProfile } from "../../types/PortfolioProfile";
import { useAppContext } from "../../contexts/app-context/components/AppContext";
import { configurationService } from "../../../appConfigService";
import { LocalStorageConstants } from "../../constants/LocalStorageConstants";
import { PortfolioRecord } from "../../types/PortfolioRecord";
import { useEffect, useState } from "react";
import axios from "axios";
import { PortfolioModel } from "../../../features/portfolio-info/types/PortfolioModel";
import { portfolioMappers } from "../../mappers/PortfolioMappers";
import { UnderlyingAssetDto } from "../../../features/portfolio-info/types/UnderlyingAssetDto";
import { useAuthContext } from "../../../features/authentication/hooks/use-auth/AuthContext";
import { useNavigate } from "react-router-dom";
import {
    portfolioPositionsCalculationService
} from "../../services/portfolio-positions-calculation-service/PortfolioPositionsCalculationService";
import { BlackScholesModelCalculationService } from "../../services/BlackScholesModelCalculationService";
import { positionsProcessor } from "../../../screens/positions-and-orders/utils/PositionsProcessor";
import { PortfolioGroupedItem } from "../../../screens/positions-and-orders/models/PortfolioGroupedItem";
import { assetService } from "../../services/AssetService";
import { PortfolioSettingsModel } from "../../../features/portfolio-info/types/PortfolioSettingsModel";
import { PortfolioDates } from "./PortfolioDates";

export function usePortfolioCommonService() {
    const [underlyingAssetDto, setUnderlyingAssetDto] = useState<UnderlyingAssetDto | null>();
    const [portfolios, setPortfolios] = useState<PortfolioModel[]>([]);
    const [selectedPortfolio, setSelectedPortfolio] = useState<PortfolioModel | null>(null);
    const [selectedPortfolioDates, setSelectedPortfolioDates] = useState<PortfolioDates | null>(null);
    const [selectedComparedPortfolio, setSelectedComparedPortfolio] = useState<PortfolioModel | null>(null);

    // todo: PortfolioProfile and PortfolioSettings
    const [portfolioProfile, setPortfolioProfile] = useState<PortfolioProfile>({
        data: []
    });
    
    // todo: PortfolioProfile and PortfolioSettings
    const [comparedPortfolioProfile, setComparedPortfolioProfile] = useState<PortfolioProfile>({
        data: []
    });
    
    const { marketDataDto } = useAppContext();
    const { userAuthData, isUserAuthenticated } = useAuthContext();
    const navigate = useNavigate();
    
    useEffect(() => {
        fetchPortfolios();
    }, [marketDataDto]);

    async function fetchPortfolios(): Promise<void> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return;
        }
        
        if (!marketDataDto) {
            return;
        }
        
        // setUnderlyingAssetDto
        const currentUnderlyingAssetName = marketDataDto.BaseAsset.BaseAsset;
        let underlyingAssetDto: UnderlyingAssetDto | null = null;

        // todo: GetUnderlyingAssetDtoByName
        await axios
            .create({
                baseURL: configurationService.configuration.baseAddress,
            })
            .get<UnderlyingAssetDto>(
                'api/underlyingAssets',
                {
                    params: { name: currentUnderlyingAssetName },
                    headers: { Authorization: `Bearer ${userAuthData?.jwtToken ?? null}`},
                },
            )
            .then(response => {
                if (typeof(response.data) !== 'string') {
                    underlyingAssetDto = response.data;
                }
            })
            .catch((error: any) => {
                console.log(error);
                return;
            });

        // todo: If null CreateUnderlyingAssetDtoByName
        if (underlyingAssetDto == null) {
            await axios
                .create({
                    baseURL: configurationService.configuration.baseAddress,
                })
                .post<UnderlyingAssetDto>(
                    'api/underlyingAssets',
                    { name: currentUnderlyingAssetName },
                    {
                        headers: {
                            ...headerValues.appJson,
                            Authorization: `Bearer ${userAuthData?.jwtToken ?? null}`
                        },
                    },
                )
                .then(response => {
                    underlyingAssetDto = response.data;
                })
                .catch((error: any) => {
                    console.log(error);
                    return;
                });
        }
        
        setUnderlyingAssetDto(underlyingAssetDto);
        
        // fetchPortfoliosByUnderlyingAsset
        axios
            .create({
                baseURL: configurationService.configuration.baseAddress,
            })
            .get<PortfolioDto[]>(
                `/api/underlying-assets/${underlyingAssetDto!.Id}/portfolios`,
                {
                    headers: { Authorization: `Bearer ${userAuthData?.jwtToken ?? null}`},
                })
            .then(async (response) => {
                const portfolioDtos = response.data;

                if (portfolioDtos == null) {
                    return;
                }

                const portfolioModels = portfolioDtos.map(x =>
                    portfolioMappers.map(x, marketDataDto.OptionInfos, marketDataDto.UnderlyingPrice));
                
                setPortfolios(portfolioModels);
                await trySetSelectedPortfolio(portfolioModels);
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    async function trySetSelectedPortfolio(portfolioModels: PortfolioModel[]): Promise<void> {
        if (portfolioModels === null || portfolioModels.length === 0) {
            return;
        }
        
        let selectedPortfolioName = localStorage.getItem(LocalStorageConstants.SelectedPortfolioName);
        let portfolioModel = portfolioModels.find(x => x.name === selectedPortfolioName);
        
        if (portfolioModel === undefined || portfolioModel === null) {
            portfolioModel = portfolioModels[0];
        }

        await setSelectedPortfolioByName(portfolioModel);
    }

    async function setSelectedPortfolioByName(portfolioModel: PortfolioModel): Promise<void> {
        if (portfolioModel.modifiedAt === null && portfolioModel.id !== 0) {
            const portfolioDto = await getPortfolioById(portfolioModel.id);

            if (portfolioDto == null || !marketDataDto) {
                return;
            }

            portfolioModel.portfolioBalanceHistory = portfolioDto.PortfolioBalanceHistory.map(portfolioMappers.mapPortfolioBalanceHistoryEntry);
            portfolioModel.positions = portfolioDto.Positions.map(x =>
                portfolioMappers.mapPosition(x, marketDataDto.OptionInfos, marketDataDto.UnderlyingPrice));
            portfolioModel.portfolioItems = portfolioDto.PortfolioItems.map(x =>
                portfolioMappers.mapOrder(x, marketDataDto.OptionInfos, marketDataDto.UnderlyingPrice));
            portfolioModel.portfolioSettings = portfolioMappers.mapSettings(portfolioDto.PortfolioSettings);
        }
        
        localStorage.setItem(LocalStorageConstants.SelectedPortfolioName, portfolioModel.name);
        
        setSelectedPortfolio(portfolioModel);
        refreshPortfolioProfile(portfolioModel);
        
        // todo: set compared portfolio
        const settings = portfolioModel?.portfolioSettings;
        
        if (!settings) {
            return;
        }
        
        if (settings.useComparedPortfolio && settings.comparedPortfolioId !== null && marketDataDto) {
            const comparedPortfolioDto = await getPortfolioById(settings.comparedPortfolioId);
            
            if (comparedPortfolioDto === null) {
                setComparedPortfolioProfile({...selectedComparedPortfolio, data: []});
                return;
            }
            
            if (!settings.useComparedPortfolio) {
                setComparedPortfolioProfile({...selectedComparedPortfolio, data: []});
            }
            
            const comparedPortfolioModel = portfolioMappers.map(comparedPortfolioDto, marketDataDto.OptionInfos, marketDataDto.UnderlyingPrice);
            setSelectedComparedPortfolio(comparedPortfolioModel);
            
            if (settings.useComparedPortfolio) {
                refreshComparedPortfolioProfile(portfolioModel, comparedPortfolioModel);
            }
        }
    }

    // todo: refactor. Similar with setSelectedPortfolioByName
    async function setSelectedComparedPortfolioByName(portfolioModel: PortfolioModel): Promise<void> {
        if (portfolioModel.modifiedAt === null && portfolioModel.id !== 0) {
            const portfolioDto = await getPortfolioById(portfolioModel.id);

            if (portfolioDto == null || !marketDataDto) {
                return;
            }

            portfolioModel.portfolioItems = portfolioDto.PortfolioItems.map(
                x => portfolioMappers.mapOrder(x, marketDataDto.OptionInfos, marketDataDto.UnderlyingPrice));
            portfolioModel.portfolioSettings = portfolioMappers.mapSettings(portfolioDto.PortfolioSettings);
        }

        setSelectedComparedPortfolio(portfolioModel);
        refreshComparedPortfolioProfile(selectedPortfolio!, portfolioModel);
    }
    
    async function getPortfolioById(id: number): Promise<PortfolioDto | null> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return null;
        }
        
        let portfolio: PortfolioDto | null = null;

        await axios
            .create({
                baseURL: configurationService.configuration.baseAddress,
            })
            .get<PortfolioDto>(
                `api/portfolios/${id}`,
                {
                    headers: { Authorization: `Bearer ${userAuthData?.jwtToken ?? null}`},
                }
            )
            .then((response) => {
                portfolio = response.data;
                
                portfolio?.PortfolioItems.map(x => {
                    const optionInfoDto = marketDataDto?.OptionInfos.find(optionInfo => optionInfo.Symbol === x.Symbol) ?? null;
                    const markImpliedVolatility = optionInfoDto?.MarkImpliedVolatility ?? null;
                    const riskFreeInterest = optionInfoDto?.RiskFreeInterest ?? BlackScholesModelCalculationService.riskFreeRate;;
                    
                    const { price, delta, gamma, vega, theta } =
                        portfolioMappers.getAssetParameters(x.Type, x.Strike, x.ExpireAt,
                            markImpliedVolatility, riskFreeInterest, marketDataDto!.UnderlyingPrice);
                    
                    x.MarkImpliedVolatility = markImpliedVolatility;
                    x.MarkPrice = price;
                    x.Delta = delta * x.Size;
                    x.Gamma = (gamma ?? 0) * x.Size;
                    x.Vega = (vega ?? 0) * x.Size;
                    x.Theta = (theta ?? 0) * x.Size;
                })
                
            })
            .catch((error: any) => {
                console.log(error);
            });

        return portfolio;
    }
    
    function refreshPortfolioProfile(portfolio: PortfolioModel | null): void {
        if (portfolio == null) {
            setPortfolioProfile({...portfolioProfile, data: []});
            return;
        }
        
        const settings = portfolio.portfolioSettings;
        
        if (settings === null) {
            return;
        }
        
        const portfolioRecords: PortfolioRecord[] = [];
        let processedPrice = settings.minUnderlyingPrice;
        
        try {
            const positions = positionsProcessor.getSummarizedPortfolioItems(portfolio);

            while (processedPrice <= settings.maxUnderlyingPrice) {
                portfolioRecords.push(getPortfolioRecord(processedPrice, positions, settings, portfolioDates => setSelectedPortfolioDates(portfolioDates)))
                processedPrice += settings.calculationStep;
            }
        }
        finally {
        }
        
        setPortfolioProfile({...portfolioProfile, data: portfolioRecords});
    }

    const getExtraCustomDatesByProportionallyDividingRemainingTime = (
        customDate1: Date,
        expiration: Date,
    ): {
        customDate2: Date,
        customDate3: Date,
    } => {
        const equalShift = (expiration.getTime() - customDate1.getTime()) / 3;
        const customDate2 = new Date(customDate1.getTime() + equalShift);
        const customDate3 = new Date(customDate1.getTime() + equalShift * 2);

        return { customDate2, customDate3 };
    }

    const getExtraCustomDatesWithSameShifts = (
        customDate1: Date,
        expiration: Date,
    ): {
        customDate2: Date,
        customDate3: Date,
    } => {
        const initialShift = (customDate1.getTime() - (new Date()).getTime());
        const customDate2 = customDate1.getTime() + initialShift <= expiration.getTime()
            ? new Date(customDate1.getTime() + initialShift)
            : expiration;
        const customDate3 = customDate1.getTime() + initialShift * 2 <= expiration.getTime()
            ? new Date(customDate1.getTime() + initialShift * 2)
            : expiration;

        return { customDate2, customDate3 };
    }
    
    const getPortfolioDates = (
        positions: PortfolioGroupedItem[],
        isTripleCustomBalanceUsingEqualShifts: boolean,
        customDate: Date,
    ): PortfolioDates => {
        const currentDate = new Date();
        const customDate1 = customDate instanceof Date ? customDate : new Date(customDate);
                
        const expirations = positions
            .filter(x =>
                assetService.isOptionType(x.type) &&
                x.currentSize !== 0 &&
                !assetService.isOptionPositionExpired(x))
            .map(x => new Date(x.expireAt!).getTime());

        if (expirations.length === 0) {
            return {
                current: currentDate,
                custom1: null,
                custom2: null,
                custom3: null,
                nearestExpiration: null,
            };
        }
        
        const nearestExpiration = new Date(Math.min(...expirations));
        
        const { customDate2, customDate3 } = isTripleCustomBalanceUsingEqualShifts
            ? getExtraCustomDatesWithSameShifts(customDate1, nearestExpiration)
            : getExtraCustomDatesByProportionallyDividingRemainingTime(customDate1, nearestExpiration);

        return {
                current: currentDate,
                custom1: customDate1,
                custom2: customDate2,
                custom3: customDate3,
                nearestExpiration: nearestExpiration
        };
    }
    
    const getPortfolioRecord = (
        processedPrice: number,
        positions: PortfolioGroupedItem[],
        portfolioSettings: PortfolioSettingsModel,
        setPortfolioDates: ((values: PortfolioDates) => void) | null,
    ): PortfolioRecord => {
        const hideCommitedBalance = portfolioSettings.hideCommitedBalance;
        const commitedBalance = portfolioSettings.commitedBalance;
        const isTripleCustomBalanceEnabled = portfolioSettings.isTripleCustomBalanceEnabled;
        
        const getBalance = (calculatedBalance: number): number => 
            hideCommitedBalance
                ? calculatedBalance - commitedBalance
                : calculatedBalance;
        
        const portfolioDates = getPortfolioDates(positions, portfolioSettings.isTripleCustomBalanceUsingEqualShifts, portfolioSettings.selectedCustomDate);
        
        setPortfolioDates?.(portfolioDates);
        
        const getImpliedVolatility = (x: PortfolioGroupedItem): number =>
            portfolioSettings.isCustomImpliedVolatilityEnabled
                ? x.customImpliedVolatility!
                : x.impliedVolatility!;
        
        return {
            price: processedPrice,
            currentDateBalance: getBalance(portfolioPositionsCalculationService.balance.calculateAtCurrent(positions, processedPrice, getImpliedVolatility)),
            customDateBalance1: portfolioDates.custom1 !== null && portfolioDates.nearestExpiration !== null && portfolioDates.custom1 < portfolioDates.nearestExpiration
                ? getBalance(portfolioPositionsCalculationService.balance.calculateAtCustom(positions, processedPrice, portfolioDates.custom1, getImpliedVolatility))
                : 0,
            customDateBalance2: isTripleCustomBalanceEnabled && portfolioDates.custom2 !== null && portfolioDates.nearestExpiration !== null && portfolioDates.custom2 < portfolioDates.nearestExpiration
                ? getBalance(portfolioPositionsCalculationService.balance.calculateAtCustom(positions, processedPrice, portfolioDates.custom2, getImpliedVolatility))
                : 0,
            customDateBalance3: isTripleCustomBalanceEnabled && portfolioDates.custom3 !== null && portfolioDates.nearestExpiration !== null && portfolioDates.custom3 < portfolioDates.nearestExpiration
                ? getBalance(portfolioPositionsCalculationService.balance.calculateAtCustom(positions, processedPrice, portfolioDates.custom3, getImpliedVolatility))
                : 0,
            expirationDateBalance: getBalance(portfolioPositionsCalculationService.balance.calculateAtExpiration(positions, processedPrice)),
            currentDelta: portfolioPositionsCalculationService.delta.calculateAtCurrent(positions, processedPrice),
            customDelta: portfolioDates.custom1 !== null && portfolioDates.nearestExpiration !== null && portfolioDates.custom1 < portfolioDates.nearestExpiration
                ? portfolioPositionsCalculationService.delta.calculateAtCustom(positions, processedPrice, portfolioDates.custom1, getImpliedVolatility)
                : 0,
            expirationDelta: portfolioPositionsCalculationService.delta.calculateAtExpiration(positions, processedPrice),
            currentGamma: portfolioPositionsCalculationService.gamma.calculateAtCurrent(positions, processedPrice),
            customGamma: portfolioDates.custom1 !== null && portfolioDates.nearestExpiration !== null && portfolioDates.custom1 < portfolioDates.nearestExpiration
                ? portfolioPositionsCalculationService.gamma.calculateAtCustom(positions, processedPrice, portfolioDates.custom1, getImpliedVolatility)
                : 0,
            expirationGamma: portfolioPositionsCalculationService.gamma.calculateAtExpiration(positions, processedPrice),
            currentVega: portfolioPositionsCalculationService.vega.calculateAtCurrent(positions, processedPrice),
            customVega: portfolioDates.custom1 !== null && portfolioDates.nearestExpiration !== null && portfolioDates.custom1 < portfolioDates.nearestExpiration
                ? portfolioPositionsCalculationService.vega.calculateAtCustom(positions, processedPrice, portfolioDates.custom1, getImpliedVolatility)
                : 0,
            expirationVega: portfolioPositionsCalculationService.vega.calculateAtExpiration(positions, processedPrice),
            currentTheta: portfolioPositionsCalculationService.theta.calculateAtCurrent(positions, processedPrice),
            customTheta: portfolioDates.custom1 !== null && portfolioDates.nearestExpiration !== null && portfolioDates.custom1 < portfolioDates.nearestExpiration
                ? portfolioPositionsCalculationService.theta.calculateAtCustom(positions, processedPrice, portfolioDates.custom1, getImpliedVolatility)
                : 0,
            expirationTheta: portfolioPositionsCalculationService.theta.calculateAtExpiration(positions, processedPrice),
            currentRho: portfolioPositionsCalculationService.rho.calculateAtCurrent(positions, processedPrice),
            customRho: portfolioDates.custom1 !== null && portfolioDates.nearestExpiration !== null && portfolioDates.custom1 < portfolioDates.nearestExpiration
                ? portfolioPositionsCalculationService.rho.calculateAtCustom(positions, processedPrice, portfolioDates.custom1, getImpliedVolatility)
                : 0,
            expirationRho: portfolioPositionsCalculationService.rho.calculateAtExpiration(positions, processedPrice),
        };
    }
    
    // todo: refactoring. The same as sibling method
    function refreshComparedPortfolioProfile(selectedPortfolio: PortfolioModel, comparedPortfolio: PortfolioModel): void {
        if (comparedPortfolio === null || !selectedPortfolio.portfolioSettings?.useComparedPortfolio) {
            setComparedPortfolioProfile({...comparedPortfolioProfile, data: []});
            return;
        }

        const selectedPortfolioSettings = selectedPortfolio.portfolioSettings;
        const comparedPortfolioSettings = comparedPortfolio.portfolioSettings;

        if (selectedPortfolioSettings === null || comparedPortfolioSettings === null) {
            return;
        }

        const portfolioRecords: PortfolioRecord[] = [];
        let processedPrice = selectedPortfolioSettings.minUnderlyingPrice;

        try {
            const positions = positionsProcessor.getSummarizedPortfolioItems(comparedPortfolio)
            
            while (processedPrice <= selectedPortfolioSettings.maxUnderlyingPrice) {
                portfolioRecords.push(getPortfolioRecord(processedPrice, positions, selectedPortfolioSettings, null));
                processedPrice += selectedPortfolioSettings.calculationStep;
            }
        }
        finally {
        }

        setComparedPortfolioProfile({...comparedPortfolioProfile, data: portfolioRecords});
    }
    
    return {
        underlyingAssetDto,
        marketDataDto,
        portfolios,
        setPortfolios,
        fetchSlimPortfolios: fetchPortfolios,
        selectedPortfolio,
        setSelectedPortfolio,
        setSelectedPortfolioByName,
        selectedComparedPortfolio,
        setSelectedComparedPortfolio,
        setSelectedComparedPortfolioByName,
        refreshPortfolioProfile,
        portfolioProfile,
        comparedPortfolioProfile,
        refreshComparedPortfolioProfile,
        setComparedPortfolioProfile,
        selectedPortfolioDates,
    };
}

const headerValues = {
    appJson: { 'Content-Type': 'application/json' },
};